import React, { useEffect } from "react";
import { Button } from "primereact/button";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import EzDialog from "../../../../common/EzDialog";
import EzControlledCalendarWithFloatingLabel from "../../../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import EzControlledDropDownWithFloatingLabel from "../../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import EzControlledInputWithFloatingLabel from "../../../../common/EzForm/EzControlledInput/WithFloatingLabel";
import EzControlledInputAreaWithFloatingLabel from "../../../../common/EzForm/EzControlledInputArea/WithFloatingLabel";
import { getFormBaseValidationRule } from "../../../../utils/form/form-utils";
import { formatDate, DATE_STRATEGY } from "../../../../utils/date";

const AddPaymentDialog = (props) => {
  const { visible, onHide, onSubmit, invoiceId, isSubmitting, invoiceSummary } = props;

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      paidAmount: null,
      paymentDate: null,
      paymentMethod: null,
      additionalInfo: "",
    },
  });

  const paidAmountValue = watch("paidAmount");
  const pendingAmount = invoiceSummary?.unpaidAmt || 0;

  useEffect(() => {
    if (!visible) {
      reset();
    }
  }, [visible, reset]);

  const getFormErrorMessage = (fieldName) => {
    return errors[fieldName] ? errors[fieldName].message : "";
  };

  const handleFormSubmit = (data) => {
    const payload = {
      invoiceId: invoiceId,
      paidAmount: data.paidAmount,
      paymentDate: data.paymentDate ? formatDate(data.paymentDate, DATE_STRATEGY.DATE_ISO) : null,
      paymentMethod: data.paymentMethod,
      userId: parseInt(localStorage.getItem("userId")),
      additionalInfo: data.additionalInfo || null,
    };
    onSubmit(payload);
  };

  const paymentMethodOptions = [
    { label: "Bank Transfer", value: "bank_transfer" },
    { label: "Check", value: "check" },
    { label: "Cash", value: "cash" },
    { label: "Credit Card", value: "credit_card" },
    { label: "Wire Transfer", value: "wire_transfer" },
    { label: "Other", value: "other" },
  ];

  return (
    <EzDialog
      header="Add Payment"
      visible={visible}
      onHide={onHide}
      width="600px"
      height="auto"
      maximizable={false}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "5px" }}
        >
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-secondary"
            onClick={onHide}
            disabled={isSubmitting}
          />
          <Button
            label="Add Payment"
            icon="pi pi-check"
            className="p-button-raised"
            onClick={handleSubmit(handleFormSubmit)}
            disabled={isSubmitting}
            loading={isSubmitting}
          />
        </div>
      }
    >
      <div className="form-demo" style={{ paddingBottom: 0 }}>
        {/* Invoice Summary Section */}
        <div style={{ 
          marginBottom: "20px", 
          padding: "10px", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "4px",
          border: "1px solid #dee2e6"
        }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "5px" }}>
                  Invoice Amount
                </div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#212529" }}>
                  {formatCurrency(invoiceSummary?.totalCommissionAmount)}
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "5px" }}>
                  Amount Paid
                </div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#28a745" }}>
                  {formatCurrency(invoiceSummary?.paidAmt)}
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "5px" }}>
                  Pending Amount
                </div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#dc3545" }}>
                  {formatCurrency(invoiceSummary?.unpaidAmt)}
                </div>
              </div>
            </Grid>
          </Grid>
        </div>

        <form className="p-fluid">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <EzControlledInputWithFloatingLabel
                name="paidAmount"
                control={control}
                rules={{
                  required: "Paid Amount is required",
                  validate: {
                    positive: (value) => parseFloat(value) > 0 || "Amount must be greater than 0",
                    notExceedPending: (value) => 
                      parseFloat(value) <= pendingAmount || 
                      `Amount cannot exceed pending amount of ${formatCurrency(pendingAmount)}`
                  }
                }}
                isError={errors["paidAmount"]}
                errorMsg={getFormErrorMessage("paidAmount")}
                label="Paid Amount ($)"
                labelClassName="ml-2"
                type="number"
                step="0.01"
                min="0"
                max={pendingAmount}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <EzControlledCalendarWithFloatingLabel
                name="paymentDate"
                control={control}
                label="Payment Date"
                rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                isError={errors.paymentDate}
                errorMsg={getFormErrorMessage("paymentDate")}
                maxDate={new Date()}
              />
            </Grid>

            <Grid item xs={12}>
              <EzControlledDropDownWithFloatingLabel
                name="paymentMethod"
                control={control}
                rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                optionLabel="label"
                optionValue="value"
                options={paymentMethodOptions}
                isError={errors["paymentMethod"]}
                errorMsg={getFormErrorMessage("paymentMethod")}
                label="Payment Method"
                labelClassName="ml-2"
                shouldFilter={false}
              />
            </Grid>

            <Grid item xs={12}>
              <EzControlledInputAreaWithFloatingLabel
                name="additionalInfo"
                control={control}
                isError={errors["additionalInfo"]}
                errorMsg={getFormErrorMessage("additionalInfo")}
                label="Additional Info (Optional)"
                labelClassName="ml-2"
                rows={3}
              />
            </Grid>
          </Grid>
        </form>
      </div>
    </EzDialog>
  );
};

export default AddPaymentDialog;
